/*
 * Creating an article.
 *
 * One form, one request, one transaction: the article row and its first revision
 * are written together. An article with no revision has no title, cannot be listed
 * sensibly and cannot go live whatever its gates say - kb.Article deliberately has
 * no Title column - so offering "create it empty" would only be offering a way to
 * make a row that needs fixing before it is useful. Parking a draft behind
 * published text is a later act, not a way to start.
 *
 * It is created hidden by default. kb.Article.IsVisible defaults to 0 in the schema
 * for the same reason, and the form does not fight that: a draft should never be one
 * mis-click away from being public.
 *
 * The slug is suggested from the title and then made unique against the addresses
 * already in use UNDER THE CHOSEN PRODUCT. kb.Article.Slug is unique only within a
 * product (UQ_Article_ProductId_Slug), so a collision is a failed insert; suffixing
 * before the request turns a rejection into a slug the author can still edit. The
 * scope matters in both directions: against a flat list of every slug in the
 * knowledge base this would suffix addresses that were free, and an author would
 * watch "exporting" become "exporting-4" because three other products had one.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48).
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ARTICLE_SLUG_MAX_LENGTH,
  fromLocalDateTimeInputValue,
  isValidSlug,
  toLocalDateTimeInputValue,
  toSlug,
  uniqueSlug,
  type ArticleVisibility,
} from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAction, useAsync } from '../hooks/useAsync.js';
import { ErrorBanner, Field, Loading } from '../components/ui.js';
import type { ArticleAddressInUse } from '../api/adminApi.js';
import { ApiError } from '../api/client.js';
import { useMessages } from '../i18n/language.js';
import { ProductSelect } from './components/ProductSelect.js';
import { SlugPrefix } from './components/SlugPrefix.js';
import { TagMultiSelect } from './components/TagMultiSelect.js';
import { MarkdownEditor } from './components/MarkdownEditor.js';
import { VisibilityField } from './components/VisibilityField.js';

export function NewArticlePage(): JSX.Element {
  const t = useMessages();
  const shared = t.admin.articles.shared;
  const words = t.admin.articles.create;
  const api = useAdminApi();
  const navigate = useNavigate();
  const action = useAction();

  const metadata = useAsync(() => api.metadata(), []);

  /*
   * Null rather than "the first product": nothing has been chosen yet, and
   * pre-selecting one would make the required field look answered while the
   * answer was this component's guess. The prefix beside the slug box reads
   * "/kb/.../" for exactly this state.
   */
  const [productId, setProductId] = useState<number | null>(null);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  // Who may read it (S46): public unless the author chooses otherwise. Offered
  // here as well as in the editor, so an article meant to be private is never
  // published public for the moment between creating it and saving its properties.
  const [visibility, setVisibility] = useState<ArticleVisibility>('public');
  const [audienceKeys, setAudienceKeys] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [content, setContent] = useState('');
  const [publishMode, setPublishMode] = useState<'hidden' | 'now' | 'schedule'>('hidden');
  const [publishAt, setPublishAt] = useState(() => toLocalDateTimeInputValue(new Date()));

  const suggestedSlug = useMemo(() => toSlug(title, ARTICLE_SLUG_MAX_LENGTH), [title]);
  const effectiveSlug = slugTouched ? slug : suggestedSlug;

  /** The other half of the address, for the prefix. Null until a product is chosen. */
  const productSlug =
    metadata.data?.products.find((product) => product.productId === productId)?.slug ?? null;

  const titleError = title.trim().length === 0 ? shared.titleRequired : undefined;
  const slugError =
    effectiveSlug.length === 0
      ? words.slugRequired
      : !isValidSlug(effectiveSlug, ARTICLE_SLUG_MAX_LENGTH)
        ? t.admin.rules.slugRequirement
        : undefined;
  // The same words the server answers with, from articlePropertiesSchema: this
  // form and a 422 should not describe the same missing field two ways.
  const productError = productId === null ? words.productRequired : undefined;
  const categoryError = categoryIds.length === 0 ? shared.categoryRequired : undefined;
  const audienceRefused =
    action.error instanceof ApiError ? action.error.fieldError('audienceKeys') : undefined;
  const audienceError =
    visibility === 'private' && audienceKeys.length === 0
      ? t.admin.articles.visibility.audienceRequired
      : audienceRefused;

  const blocked = Boolean(
    titleError ||
      slugError ||
      productError ||
      categoryError ||
      (visibility === 'private' && audienceKeys.length === 0),
  );

  const submit = async () => {
    // `blocked` already covers this; the explicit null check is what narrows the
    // type, and it has to be a check rather than an assertion because productId
    // being set is the whole of what makes the suffixing below product-scoped.
    if (blocked || productId === null) return;

    const publishAtUtc =
      publishMode === 'schedule'
        ? (fromLocalDateTimeInputValue(publishAt) ?? new Date().toISOString())
        : new Date().toISOString();

    // A failed lookup is not a reason to refuse the create: the unique index is
    // still the authority, and the server will answer 409 on a genuine collision.
    const taken = await api.articleSlugs().catch(() => [] as ArticleAddressInUse[]);

    // Only the addresses under the chosen product can collide with this one. The
    // rest are a different product's namespace and must not move the suffix.
    const takenHere = taken
      .filter((address) => address.productId === productId)
      .map((address) => address.slug);
    const freeSlug = uniqueSlug(effectiveSlug, takenHere, ARTICLE_SLUG_MAX_LENGTH);

    const created = await action.run(() =>
      api.createArticle({
        slug: freeSlug,
        productId,
        categoryIds,
        publishAtUtc,
        isVisible: publishMode !== 'hidden',
        visibility,
        audienceKeys: visibility === 'private' ? audienceKeys : [],
        title: title.trim(),
        content,
        changeNote: 'Created.',
      }),
    );

    if (created) navigate(`/admin/articles/${created.articleId}`);
  };

  if (metadata.loading && !metadata.data) return <Loading label={words.loadingMetadata} />;
  if (metadata.error) return <ErrorBanner error={metadata.error} onRetry={metadata.reload} />;

  const missingMetadata =
    (metadata.data?.products.length ?? 0) === 0 ||
    (metadata.data?.categories.length ?? 0) === 0;

  if (missingMetadata) {
    return (
      <div className="card">
        <h1>{words.setUpMetadata}</h1>
        <p>{words.setUpMetadataBody}</p>
        <ul>
          {(metadata.data?.products.length ?? 0) === 0 && <li>{words.noProductTags}</li>}
          {(metadata.data?.categories.length ?? 0) === 0 && <li>{words.noCategoryTags}</li>}
        </ul>
        <Link className="button button-primary" to="/admin/metadata">
          {words.goToMetadata}
        </Link>
      </div>
    );
  }

  const storedAt = fromLocalDateTimeInputValue(publishAt);

  return (
    <>
      <div className="page-header">
        <div>
          <nav className="breadcrumb">
            <Link to="/admin/articles">{shared.articles}</Link>
            <span aria-hidden="true">/</span>
            <span>{words.breadcrumbNew}</span>
          </nav>
          <h1>{words.heading}</h1>
          <p className="muted">{words.intro}</p>
        </div>
      </div>

      {/* An audience refused by the server shows under its field instead (S46). */}
      {action.error && !audienceRefused && (
        <ErrorBanner error={action.error} onDismiss={action.clearError} />
      )}

      <div className="split-two">
        <div className="card">
          <div className="card-header">
            <h3>{words.content}</h3>
          </div>

          <Field label={shared.title} htmlFor="new-title" required error={titleError}>
            <input
              id="new-title"
              type="text"
              value={title}
              maxLength={300}
              autoFocus
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>

          {/*
            The hint no longer recites the address - the prefix and the box beside
            it show it being built. What is left is the part neither of them can
            show: where the slug comes from, and the fact that the number appended
            on a collision is decided within the chosen product and nowhere wider.
          */}
          <Field
            label={shared.slug}
            htmlFor="new-slug"
            required
            error={slugError}
            hint={words.slugHint}
          >
            <SlugPrefix productSlug={productSlug}>
              <input
                id="new-slug"
                type="text"
                value={effectiveSlug}
                maxLength={ARTICLE_SLUG_MAX_LENGTH}
                onChange={(event) => {
                  setSlugTouched(true);
                  setSlug(event.target.value);
                }}
                onBlur={(event) => setSlug(toSlug(event.target.value, ARTICLE_SLUG_MAX_LENGTH))}
              />
            </SlugPrefix>
          </Field>

          <MarkdownEditor value={content} onChange={setContent} disabled={action.running} />
        </div>

        <div>
          <div className="card">
            <div className="card-header">
              <h3>{shared.properties}</h3>
            </div>

            <ProductSelect
              error={productError}
              options={(metadata.data?.products ?? []).map((product) => ({
                id: product.productId,
                name: product.name,
                isActive: product.isActive,
              }))}
              selected={productId}
              onChange={setProductId}
            />

            <TagMultiSelect
              label={shared.categoryTags}
              kind="category"
              error={categoryError}
              options={(metadata.data?.categories ?? []).map((category) => ({
                id: category.categoryId,
                name: category.name,
                isActive: category.isActive,
              }))}
              selected={categoryIds}
              onChange={setCategoryIds}
            />

            <VisibilityField
              visibility={visibility}
              audienceKeys={audienceKeys}
              onChange={(nextVisibility, nextKeys) => {
                setVisibility(nextVisibility);
                setAudienceKeys(nextKeys);
              }}
              options={metadata.data?.audiences ?? null}
              error={audienceError}
            />
          </div>

          <div className="card">
            <div className="card-header">
              <h3>{shared.publishing}</h3>
            </div>

            <div className="field">
              <label htmlFor="publish-mode">{words.whenVisible}</label>
              <select
                id="publish-mode"
                value={publishMode}
                onChange={(event) =>
                  setPublishMode(event.target.value as 'hidden' | 'now' | 'schedule')
                }
              >
                <option value="hidden">{words.modeHidden}</option>
                <option value="now">{words.modeNow}</option>
                <option value="schedule">{words.modeSchedule}</option>
              </select>
              <p className="field-hint">
                {publishMode === 'hidden'
                  ? words.hintHidden
                  : publishMode === 'now'
                    ? words.hintNow
                    : words.hintSchedule}
              </p>
            </div>

            {publishMode === 'schedule' && (
              <Field
                label={shared.publishAtLocal}
                htmlFor="publish-at"
                hint={storedAt ? words.storedAs(storedAt) : undefined}
              >
                <input
                  id="publish-at"
                  type="datetime-local"
                  value={publishAt}
                  onChange={(event) => setPublishAt(event.target.value)}
                />
              </Field>
            )}
          </div>

          <div className="button-row">
            <button
              type="button"
              className="button button-primary"
              onClick={() => void submit()}
              disabled={blocked || action.running}
            >
              {action.running ? words.creating : words.createArticle}
            </button>
            <Link className="button button-quiet" to="/admin/articles">
              {shared.cancel}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
